/**
 * The Skimlinks `xcust` value, built in one place for the web and the app.
 *
 * Skimlinks documents `xcust` as at most 50 characters of letters, digits,
 * `_` and `|` (support.skimlinks.com/hc/en-us/articles/360005908078). The
 * shapes this replaced broke both rules: the app sent
 * `<customer uuid>|<64-char product hash>|app`, about 105 characters signed in,
 * and the web packed hyphenated UUIDs and slugs.
 *
 * ## Format
 *
 *     <surface>|<product>|<customer>[|<cohort>]
 *
 * - `surface` is `app` or `web`, and comes FIRST so that no truncation can
 *   ever cut it off. It is the one field reporting cannot do without: it is
 *   how `scripts/skimlinks-report.mjs` splits app sales from web sales.
 * - `product` is the first 12 letters or digits of the product id. For a
 *   64-hex product hash that is a 48-bit prefix, unique across any catalogue
 *   this site will hold, and still a prefix match against the full id.
 * - `customer` is the first 12 letters or digits of the customer id, or
 *   `a_` plus 10 of an anonymous id (`anon_...`), so a signed-in sale and an
 *   anonymous one never read alike. Empty when there is neither.
 * - `cohort` is the web's UTM cohort, with every character outside the
 *   allowed set turned into `_`, cut to whatever room the cap leaves. Omitted
 *   when empty.
 *
 * Examples: `app|3f9a1c0be7d2|8c1e4b7a2f90`, `web|3f9a1c0be7d2|`,
 * `web|3f9a1c0be7d2|a_k2j9x0q1zz|launch_instagram`.
 *
 * The reader is `scripts/lib/xcust.mjs`, which also still reads every older
 * shape: sales clicked before this shipped keep reporting the old values, and
 * installs on older app builds keep emitting them for as long as they exist.
 */

export const XCUST_MAX_LENGTH = 50;

/** Characters Skimlinks accepts in `xcust`, the separator included. */
export const XCUST_ALLOWED = /^[A-Za-z0-9_|]*$/;

export type XcustSurface = 'app' | 'web';

/** Room for each id. Two of them plus `app|`, `|` and `|` is 30 of the 50. */
const ID_LENGTH = 12;
const ANON_PREFIX = 'anon_';

export interface XcustFields {
  surface: XcustSurface;
  /** Product id or slug. Only its first 12 letters and digits travel. */
  productId?: string | null;
  /** Customer id, or an `anon_...` id. Only 12 characters travel. */
  customerId?: string | null;
  /** UTM cohort label (web only). */
  cohort?: string | null;
}

/** Letters and digits only. A separator or a hyphen inside an id would corrupt the split. */
function alnum(value: string): string {
  return value.replace(/[^A-Za-z0-9]/g, '');
}

function customerToken(id: string | null | undefined): string {
  if (!id) return '';
  if (id.startsWith(ANON_PREFIX)) {
    const rest = alnum(id.slice(ANON_PREFIX.length)).slice(0, ID_LENGTH - 2);
    return rest ? `a_${rest}` : '';
  }
  return alnum(id).slice(0, ID_LENGTH);
}

export function buildXcust(fields: XcustFields): string {
  const surface: XcustSurface = fields.surface === 'app' ? 'app' : 'web';
  const product = alnum(fields.productId ?? '').slice(0, ID_LENGTH);
  const customer = customerToken(fields.customerId);
  const head = `${surface}|${product}|${customer}`;

  const cohort = (fields.cohort ?? '').replace(/[^A-Za-z0-9_]/g, '_');
  const room = XCUST_MAX_LENGTH - head.length - 1;
  const value = cohort && room > 0 ? `${head}|${cohort.slice(0, room)}` : head;

  // The fields above are bounded so this never bites today. It is here so
  // the cap is a property of the function rather than of today's field
  // widths, and the surface survives it because it is first.
  return value.slice(0, XCUST_MAX_LENGTH);
}
